import { ApiChannel, GiftCard } from '@vend/types'
import { VdToastNotificationService } from '@vend/vend.ui'

import { ERRORS } from '../../components/gift-cards/errors'
import { GiftCardsResource } from '../../components/gift-cards/gift-cards-resource/gift-cards-resource.service'
import { GIFTCARD_PROVIDER_SETTINGS } from '../../components/gift-cards/provider-settings'
import * as WR_GIFT_CARDS from '../../constants'
import { channelsStore } from '../../factories/database/channels-store'
import { AbstractGiftCardsController as AbstractGiftCards } from './abstract-gift-cards'

const errNoBigCommerceChannel = new Error(
  'Sorry, could not find a BigCommerce channel.'
)

/**
 * Controller for handling BigCommerce Gift Certificates.
 *
 * A note on naming. When referred to in code, it's "BigCommerce Gift Certs".
 * BigCommerce should be capitalized as such for title case only,
 * For snake, camel and hyphen case treat "BigCommerce" as a single word.
 * ✔ BigCommerceGiftCert
 * ✔ bigcommerceGiftCert
 * ✔ bigcommerce-gift-cert.ts
 * ✔ bigcommerce_gift_cert.json
 * ✘ bigCommerceGiftCerts
 * ✘ big-commerce-gift-cert.ts
 * ✘ big_commerce_gift_certs.json
 */
export class BigCommerceGiftCertsController extends AbstractGiftCards {
  constructor(
    close: () => any,
    options: any,
    registerManager: any,
    private readonly vdToastNotificationService: VdToastNotificationService,
    private readonly giftCardsResource: GiftCardsResource
  ) {
    'ngInject'
    super(
      GIFTCARD_PROVIDER_SETTINGS.BIGCOMMERCE,
      close,
      options.amount,
      /^[-0-9a-zA-Z]{1,15}$/,
      0,
      15,
      options.workflow,
      registerManager.getActiveOutlet(),
      {
        min: 0.01,
        decimalPlaces: 2,
        allowedPrefix: registerManager.getActiveOutlet().currency_symbol,
        max: undefined,
      }
    )
  }

  /**
   * Gives us the search modal title
   */
  public getSearchModalTitle(): string {
    return 'Enter a Gift Certificate Code'
  }

  /**
   * Gives us the search modal text
   */
  public getSearchModalText(): string {
    return 'Check an existing certificate’s status and balance.'
  }

  /**
   * Returns the display status of the gift card.
   */
  public getGiftCardDisplayStatus(): string {
    const status = (this.giftCard as GiftCard).status
    return status
  }

  /**
   * Retrieve the details of the current gift card.
   */
  public retrieveGiftCard() {
    const giftCardCode = this.giftCard.code

    this.waiting = true

    channelsStore
      .getByChannelType('bigcommerce')
      .then((channels: ApiChannel[]): string => {
        if (channels.length < 1) {
          throw errNoBigCommerceChannel
        }
        return channels[0].id
      })
      .then((channelId: string) => {
        return this.giftCardsResource.getGiftCardByCode(channelId, giftCardCode)
      })
      .then((giftCard: GiftCard | null) => {
        if (!giftCard) {
          this.vdToastNotificationService.negative({
            message:
              'Gift certificate not activated. Please check again or enter another number.',
          })
          return
        }
        this.giftCard = giftCard
        this.state = WR_GIFT_CARDS.states.VIEW_DETAILS
      })
      .catch((response: any) => {
        if (response === errNoBigCommerceChannel) {
          this.vdToastNotificationService.negative({
            message: 'Sorry, could not find a BigCommerce channel.',
          })
          return
        }
        switch (response.status) {
          case 0: {
            const message =
              'Gift certificates can not be redeemed when the Sell screen is offline.'

            this.vdToastNotificationService.negative({ message })
            break
          }

          default:
            this.vdToastNotificationService.negative({
              message: 'Sorry, something went wrong. Please try again.',
            })
            break
        }
      })
      .finally(() => {
        this.waiting = false
      })
  }

  /**
   * Makes a payment against the provided gift card.
   */
  public payWithGiftCard() {
    const payableGiftCardAmount = this.getPayableAmount()
    const giftCardId = (this.giftCard as GiftCard).id

    // Prevent modal from being closed while we wait for the transaction to complete
    this.dismissible = false
    this.waiting = true

    channelsStore
      .getByChannelType('bigcommerce')
      .then((channels: ApiChannel[]): string => {
        if (channels.length < 1) {
          throw errNoBigCommerceChannel
        }
        return channels[0].id
      })
      .then((channelId: string) => {
        return this.giftCardsResource.redeemGiftCard(
          channelId,
          giftCardId,
          Number(payableGiftCardAmount)
        )
      })
      .then((transactionId: string) => {
        this.close({
          amountTendered: payableGiftCardAmount,
          source: 'BigCommerce Gift Certificate',
          source_id: transactionId,
        })
      })
      .catch((error: Error) => {
        switch (error.message) {
          case ERRORS.EXPIRED:
            this.vdToastNotificationService.negative({
              message: 'Sorry, Gift Certificate has expired.',
            })
            break
          case ERRORS.INSUFFICIENT_FUNDS:
            this.vdToastNotificationService.negative({
              message: 'Sorry, insufficient funds available.',
            })
            break
          case ERRORS.NOT_FOUND:
            this.vdToastNotificationService.negative({
              message: 'Sorry, Gift Certificate couldn’t be found.',
            })
            break
          default:
            this.vdToastNotificationService.negative({
              message: 'Sorry, something went wrong. Please try again.',
            })
            break
        }

        this.waiting = false
        this.dismissible = true
      })
  }

  /**
   * Returns true if the gift card can be recharged, false otherwise.
   */
  public isRechargeable(): boolean {
    return true
  }
}
